import { createContext, use, useId } from 'react';
import { Controller, FormProvider, useFormContext, useFormState } from 'react-hook-form';
import { Slot } from 'radix-ui';
import { CircleAlert } from 'lucide-react';
import { Label } from './label.jsx';
import { Checkbox, Input, Textarea } from './input.jsx';
import { Select } from './select.jsx';
import { Alert, AlertDescription } from './alert.jsx';
import { cn } from '../lib/utils.js';

/** <Form {...form}> – thin alias of react-hook-form's FormProvider. */
export const Form = FormProvider;

/** @type {import('react').Context<{ name: string, id: string } | null>} */
const FieldContext = createContext(null);

function useField() {
  const field = use(FieldContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: field?.name });
  if (!field) throw new Error('Form field components must be used inside <FormField>');
  const state = getFieldState(field.name, formState);
  return {
    ...field,
    ...state,
    controlId: `${field.id}-control`,
    descriptionId: `${field.id}-description`,
    messageId: `${field.id}-message`,
  };
}

/**
 * Generic RHF field. `render` receives the controller field props.
 * Use FormItem/FormLabel/FormControl/FormDescription/FormMessage inside.
 * @param {{ name: string, control?: any, rules?: any, render: (ctx: { field: any, fieldState: any }) => import('react').ReactElement }} props
 */
export function FormField({ name, control, rules, render }) {
  const id = useId();
  return (
    <FieldContext value={{ name, id }}>
      <Controller name={name} control={control} rules={rules} render={render} />
    </FieldContext>
  );
}

/** @param {import('react').ComponentProps<'div'>} props */
export function FormItem({ className, ...props }) {
  return <div data-slot="form-item" className={cn('flex flex-col gap-1.5', className)} {...props} />;
}

/** @param {import('react').ComponentProps<typeof Label>} props */
export function FormLabel({ className, ...props }) {
  const { error, controlId } = useField();
  return <Label data-error={Boolean(error)} className={cn('data-[error=true]:text-destructive', className)} htmlFor={controlId} {...props} />;
}

/** @param {import('react').ComponentProps<typeof Slot.Root>} props */
export function FormControl(props) {
  const { error, controlId, descriptionId, messageId } = useField();
  return (
    <Slot.Root
      data-slot="form-control"
      id={controlId}
      aria-describedby={error ? `${descriptionId} ${messageId}` : descriptionId}
      aria-invalid={Boolean(error)}
      {...props}
    />
  );
}

/** Help text below the input. @param {import('react').ComponentProps<'p'>} props */
export function FormDescription({ className, ...props }) {
  const { descriptionId } = useField();
  return <p id={descriptionId} data-slot="form-description" className={cn('text-xs text-muted-foreground', className)} {...props} />;
}

/** Field error (client validation or API field error). @param {import('react').ComponentProps<'p'>} props */
export function FormMessage({ className, children, ...props }) {
  const { error, messageId } = useField();
  const body = error ? String(error.message ?? '') : children;
  if (!body) return null;
  return (
    <p id={messageId} data-slot="form-message" className={cn('text-xs font-medium text-destructive', className)} {...props}>
      {body}
    </p>
  );
}

/**
 * Convenience text input field: label on top, help text, error below.
 * @param {{ name: string, label: import('react').ReactNode, description?: import('react').ReactNode } & Omit<import('react').ComponentProps<'input'>, 'name'>} props
 */
export function InputField({ name, label, description, className, ...inputProps }) {
  const { control } = useFormContext();
  return (
    <FormField
      name={name}
      control={control}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input {...inputProps} {...field} value={field.value ?? ''} />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/**
 * Multi-line text field.
 * @param {{ name: string, label: import('react').ReactNode, description?: import('react').ReactNode } & Omit<import('react').ComponentProps<'textarea'>, 'name'>} props
 */
export function TextareaField({ name, label, description, className, ...textareaProps }) {
  const { control } = useFormContext();
  return (
    <FormField
      name={name}
      control={control}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Textarea {...textareaProps} {...field} value={field.value ?? ''} />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/**
 * Native select field. `options`: [{ value, label }]; `placeholder` adds an empty first option.
 * @param {{ name: string, label: import('react').ReactNode, description?: import('react').ReactNode, options: Array<{ value: string, label: string }>, placeholder?: string } & Omit<import('react').ComponentProps<'select'>, 'name'>} props
 */
export function SelectField({ name, label, description, options, placeholder, className, ...selectProps }) {
  const { control } = useFormContext();
  return (
    <FormField
      name={name}
      control={control}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Select {...selectProps} {...field} value={field.value ?? ''}>
              {placeholder !== undefined && <option value="">{placeholder}</option>}
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/**
 * Checkbox with label on the right (boolean form value).
 * @param {{ name: string, label: import('react').ReactNode, description?: import('react').ReactNode, className?: string, disabled?: boolean }} props
 */
export function CheckboxField({ name, label, description, className, disabled }) {
  const { control } = useFormContext();
  return (
    <FormField
      name={name}
      control={control}
      render={({ field }) => (
        <FormItem className={className}>
          <div className="flex items-start gap-2">
            <FormControl>
              <Checkbox
                className="mt-0.5"
                name={field.name}
                ref={field.ref}
                checked={Boolean(field.value)}
                onChange={(e) => field.onChange(e.target.checked)}
                onBlur={field.onBlur}
                disabled={disabled}
              />
            </FormControl>
            <div className="flex flex-col gap-0.5">
              <FormLabel className="font-normal">{label}</FormLabel>
              {description && <FormDescription>{description}</FormDescription>}
            </div>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** Shows `errors.root.server` (set by applyProblemToForm). */
export function FormRootError({ className }) {
  const { errors } = useFormState();
  const message = errors.root?.server?.message;
  if (!message) return null;
  return (
    <Alert variant="destructive" className={className}>
      <CircleAlert aria-hidden="true" />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

/**
 * Maps an API error (ProblemError, duck-typed) onto react-hook-form:
 * 422 field errors → setError(field); everything else → root.server.
 * Returns true if at least one field error was applied.
 * @param {any} error
 * @param {(name: any, error: { type: string, message: string }, options?: { shouldFocus?: boolean }) => void} setError
 * @param {{ fields?: string[], fallback?: string }} [options]
 */
export function applyProblemToForm(error, setError, options = {}) {
  const { fields, fallback = 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.' } = options;
  const fieldErrors = error?.status === 422 && error?.errors ? error.errors : {};
  let applied = false;
  let first = true;
  const unmatched = [];
  for (const [field, messages] of Object.entries(fieldErrors)) {
    const message = Array.isArray(messages) ? messages[0] : String(messages);
    if (!fields || fields.includes(field)) {
      setError(field, { type: 'server', message }, { shouldFocus: first });
      first = false;
      applied = true;
    } else {
      unmatched.push(message);
    }
  }
  if (!applied) {
    const message =
      unmatched[0] ||
      (error?.status === 429 ? 'Zu viele Versuche. Bitte warten Sie einen Moment.' : error?.detail || error?.title || error?.message || fallback);
    setError('root.server', { type: 'server', message });
  }
  return applied;
}
